/**
 * Transactions services — receivables, payments, and movements.
 *
 * Each service exposes a `list/create/update/remove` quartet so the
 * matching React Query hooks can be derived mechanically.
 *
 * @module services/transactionsService
 */
import { api } from "../lib/api";

/**
 * @typedef {Object} ListParams
 * @property {number} [limit]
 * @property {number} [skip]
 */

export const receivablesService = {
  /**
   * @param {ListParams} [params]
   * @returns {Promise<import("./types").Receivable[]>}
   */
  list: (params = {}) =>
    api.get("/receivables", { params }).then((r) => r.data),

  /**
   * Cursor-based page for infinite scroll.
   * @param {{cursor?: string|null, limit?: number, status?: string, search?: string, date_from?: string, date_to?: string}} params
   * @returns {Promise<{items: import("./types").Receivable[], next_cursor: string|null, has_more: boolean, limit: number}>}
   */
  listPage: ({ cursor = "", limit = 30, status, search, date_from, date_to, tag } = {}) => {
    const params = { cursor: cursor ?? "", limit };
    if (status) params.status = status;
    if (search) params.search = search;
    if (date_from) params.date_from = date_from;
    if (date_to) params.date_to = date_to;
    if (tag) params.tag = tag;
    return api.get("/receivables", { params }).then((r) => r.data);
  },

  /**
   * @param {Omit<import("./types").Receivable,"id"|"status">} payload
   * @returns {Promise<import("./types").Receivable>}
   */
  create: (payload) => api.post("/receivables", payload).then((r) => r.data),

  /**
   * @param {string} id
   * @param {Omit<import("./types").Receivable,"id"|"status">} payload
   * @returns {Promise<import("./types").Receivable>}
   */
  update: (id, payload) => api.put(`/receivables/${id}`, payload).then((r) => r.data),

  /** @param {string} id @returns {Promise<{ok:true}>} */
  remove: (id) => api.delete(`/receivables/${id}`).then((r) => r.data),

  /**
   * Mark collected. ``accountId`` is required when ``r.account_id`` is
   * null — the picker dialog collects it from the user and we send it
   * here. The backend persists it on the receivable for audit.
   * @param {string} id
   * @param {string|null} [accountId]
   */
  collect: (id, accountId = null) =>
    api.post(`/receivables/${id}/collect`, accountId ? { account_id: accountId } : {}).then((r) => r.data),

  /**
   * iter-211 — Cancel a recurring receivable (subscription churn).
   * Backend stamps `cancelled_at`, flips `recurring=false`, sets
   * `status='cancelled'` and emits a `churn_events` row.
   * @param {string} id
   * @param {string} [reason]  free-text reason (max 280 chars)
   */
  cancelSubscription: (id, reason = "") =>
    api.post(`/receivables/${id}/cancel`, { reason }).then((r) => r.data),
};

export const paymentsService = {
  /**
   * @param {ListParams} [params]
   * @returns {Promise<import("./types").Payment[]>}
   */
  list: (params = {}) =>
    api.get("/payments", { params }).then((r) => r.data),

  /**
   * Cursor-based page for infinite scroll.
   */
  listPage: ({ cursor = "", limit = 30, status, search, date_from, date_to, tag } = {}) => {
    const params = { cursor: cursor ?? "", limit };
    if (status) params.status = status;
    if (search) params.search = search;
    if (date_from) params.date_from = date_from;
    if (date_to) params.date_to = date_to;
    if (tag) params.tag = tag;
    return api.get("/payments", { params }).then((r) => r.data);
  },

  /**
   * @param {Omit<import("./types").Payment,"id"|"status">} payload
   * @returns {Promise<import("./types").Payment>}
   */
  create: (payload) => api.post("/payments", payload).then((r) => r.data),

  /**
   * @param {string} id
   * @param {Omit<import("./types").Payment,"id"|"status">} payload
   * @returns {Promise<import("./types").Payment>}
   */
  update: (id, payload) => api.put(`/payments/${id}`, payload).then((r) => r.data),

  /** @param {string} id @returns {Promise<{ok:true}>} */
  remove: (id) => api.delete(`/payments/${id}`).then((r) => r.data),

  /**
   * Mark paid. The 2nd argument is an optional funding override:
   *   • ``{account_id: "..."}`` debits a cash account.
   *   • ``{debt_id: "..."}`` increases a debt's balance instead.
   * Backend persists whichever pointer is provided on the payment doc.
   * @param {string} id
   * @param {{account_id?: string, debt_id?: string}} [funding]
   */
  markPaid: (id, funding = null) => {
    const body = {};
    if (funding?.account_id) body.account_id = funding.account_id;
    if (funding?.debt_id) body.debt_id = funding.debt_id;
    return api.post(`/payments/${id}/pay`, body).then((r) => r.data);
  },

  /**
   * @param {string} id
   * @param {string} new_due_date ISO date string
   * @returns {Promise<import("./types").Payment>}
   */
  postpone: (id, new_due_date) =>
    api.post(`/payments/${id}/postpone`, { new_due_date }).then((r) => r.data),
};

export const movementsService = {
  /**
   * @param {number} [limit]
   * @returns {Promise<import("./types").Movement[]>}
   */
  list: (limit = 100) =>
    api.get("/movements", { params: { limit } }).then((r) => r.data),

  /**
   * Cursor-based page for infinite scroll (date DESC).
   */
  listPage: ({ cursor = "", limit = 30 } = {}) =>
    api.get("/movements", { params: { cursor: cursor ?? "", limit } }).then((r) => r.data),

  /**
   * @param {Omit<import("./types").Movement,"id">} payload
   * @returns {Promise<import("./types").Movement>}
   */
  create: (payload) => api.post("/movements", payload).then((r) => r.data),

  /**
   * @param {string} text
   * @returns {Promise<import("./types").Movement>}
   */
  quick: (text) => api.post("/movements/quick", { text }).then((r) => r.data),

  /**
   * Edit an existing historical movement. Backend adjusts the primary cash
   * account by the signed delta.
   * @param {string} id
   * @param {Partial<import("./types").Movement>} payload
   */
  update: (id, payload) => api.put(`/movements/${id}`, payload).then((r) => r.data),

  /**
   * Permanently delete a historical movement. Backend reverses its
   * cash-account effect.
   * @param {string} id
   */
  remove: (id) => api.delete(`/movements/${id}`).then((r) => r.data),
};

/**
 * Tags aggregation — returns { top: [{tag,n}], by_kind: {...} } for the
 * current user. Powers tag autocomplete + filter dropdowns.
 */
export const tagsService = {
  list: () => api.get("/tags").then((r) => r.data),
};
